import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { externalApi } from '../../services/api';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { formatINR } from '../../utils/formatters';
import { Warning, ArrowsClockwise } from '@phosphor-icons/react';

export default function ChartinkTab() {
  const navigate = useNavigate();
  const [scans, setScans] = useState([]);
  const [activeScan, setActiveScan] = useState('bullish_breakout');
  const [scanData, setScanData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    externalApi.chartinkScans()
      .then((r) => setScans(r.data.scans || []))
      .catch((e) => console.error('chartink scans list failed', e));
  }, []);

  const runScan = useCallback(async (key) => {
    setLoading(true);
    setActiveScan(key);
    try {
      const { data } = await externalApi.chartinkRun(key);
      setScanData(data);
    } catch (e) {
      console.error('chartink scan run failed', e);
      setScanData({ error: 'Failed to run scan' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { runScan('bullish_breakout'); }, [runScan]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6" data-testid="chartink-tab-content">
      <div className="space-y-2">
        <p className="text-[10px] uppercase tracking-[0.2em] text-[#64748B] mb-3">Available Scans</p>
        {scans.map((s) => (
          <button
            key={s.key}
            onClick={() => runScan(s.key)}
            data-testid={`chartink-scan-${s.key}`}
            className={`w-full text-left p-3 border transition-all ${
              activeScan === s.key
                ? 'border-[#2563EB] bg-[#2563EB]/10 text-white'
                : 'border-white/10 text-[#94A3B8] hover:border-white/30 hover:text-white'
            }`}
          >
            <div className="text-sm font-medium">{s.label}</div>
            <div className="text-[11px] text-[#64748B] mt-0.5 leading-snug">{s.description}</div>
          </button>
        ))}
      </div>

      <div>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-white">{scanData?.label || 'Chartink Scan'}</h3>
            <p className="text-xs text-[#64748B] mt-0.5">
              {scanData?.count ?? 0} matches • Data via chartink.com • Cached 10 min
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => runScan(activeScan)}
            className="border-white/20 text-white hover:bg-white/5"
            data-testid="chartink-refresh"
          >
            <ArrowsClockwise size={16} className="mr-1.5" />
            Refresh
          </Button>
        </div>

        {loading && <div className="text-[#64748B] text-sm">Running scan…</div>}
        {!loading && scanData?.error && (
          <Card className="p-4 bg-[#EF4444]/10 border-[#EF4444]/30 text-[#EF4444] text-sm">
            <Warning size={16} weight="fill" className="inline mr-2" />
            {scanData.error}
          </Card>
        )}

        {!loading && scanData?.results && (
          <ChartinkResultsTable results={scanData.results} onRowClick={(sym) => navigate(`/stock/${sym}`)} />
        )}
      </div>
    </div>
  );
}

function ChartinkResultsTable({ results, onRowClick }) {
  if (results.length === 0) {
    return (
      <div className="border border-white/10 py-6 text-center text-[#64748B] text-sm">
        No stocks matched this scan today.
      </div>
    );
  }
  return (
    <div className="border border-white/10 overflow-hidden">
      <table className="w-full text-sm" data-testid="chartink-results-table">
        <thead>
          <tr className="bg-white/5 text-[#94A3B8] text-[11px] uppercase tracking-wider">
            <th className="text-left py-2 px-3">Symbol</th>
            <th className="text-left py-2 px-3">Name</th>
            <th className="text-right py-2 px-3">Price</th>
            <th className="text-right py-2 px-3">Change</th>
            <th className="text-right py-2 px-3">Volume</th>
          </tr>
        </thead>
        <tbody>
          {results.map((r) => (
            <ChartinkRow key={r.symbol || r.name} row={r} onClick={onRowClick} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ChartinkRow({ row, onClick }) {
  const changePct = row.change_percent || 0;
  const changeColor = changePct >= 0 ? 'text-[#10B981]' : 'text-[#EF4444]';
  return (
    <tr
      className="border-t border-white/5 hover:bg-white/5 cursor-pointer"
      onClick={() => onClick(row.symbol)}
      data-testid={`chartink-row-${row.symbol}`}
    >
      <td className="py-2 px-3 font-mono text-white">{row.symbol}</td>
      <td className="py-2 px-3 text-[#94A3B8]">{row.name}</td>
      <td className="py-2 px-3 text-right text-white">
        {row.price ? formatINR(row.price) : '—'}
      </td>
      <td className={`py-2 px-3 text-right font-mono ${changeColor}`}>
        {changePct >= 0 ? '+' : ''}{changePct.toFixed(2)}%
      </td>
      <td className="py-2 px-3 text-right text-[#64748B] font-mono text-xs">
        {(row.volume || 0).toLocaleString('en-IN')}
      </td>
    </tr>
  );
}
